/** The film player's arithmetic (docs/SPEC.md §7, §19): the film's clock
 *  over its scenes, what is on screen at a moment (one scene, or two at a
 *  transition), the drawing steps that make that picture, the samples of a
 *  sidecar at a local time, and the transport. Nothing here imports three
 *  or touches the DOM, so every rule is unit-tested.
 *
 *  Time: a scene's own time is **local** (0 to its `seconds`, §7.2); the
 *  film's time is the manifest's clock (`start` of each scene, whole frames,
 *  `timeline.py`). A transition is the overlap of two scenes on that clock:
 *  scene k+1 starts before scene k ends, and both play their own local time
 *  through it. */
import type {
  CameraRow,
  Manifest,
  ManifestTransition,
  ObjectRow,
  PersonRow,
  Vec3,
  WipeDirection,
} from "./manifest";

// --------------------------------------------------------------- the clock

/** One scene on the film's clock. */
export interface Segment {
  index: number;
  id: string;
  seconds: number;
  /** Film time of its local 0. */
  start: number;
  /** Film time at which its last frame ends. */
  end: number;
}

export function segments(m: Manifest): Segment[] {
  return m.scenes.map((scene, index) => ({
    index,
    id: scene.id,
    seconds: scene.seconds,
    start: scene.first_frame / m.fps,
    end: (scene.first_frame + scene.frames) / m.fps,
  }));
}

/** The film's length on its clock, seconds: where the last scene ends. */
export function filmLength(segs: readonly Segment[]): number {
  return segs.length === 0 ? 0 : segs[segs.length - 1].end;
}

/** A scene at one of its local moments. */
export interface Shot {
  scene: number;
  t: number;
}

/** A camera pose in the film's axes; `fovY` degrees across the height. */
export interface CameraPose {
  from: Vec3;
  to: Vec3;
  fovY: number;
}

/** A colour laid over the whole picture (the film's leader and trailer). */
export interface Veil {
  color: string;
  alpha: number;
}

/** What is on screen at a moment. `p` is a transition's progress, 0..1. */
export type Composition =
  | { kind: "one"; shot: Shot; veil: Veil | null }
  | {
      kind: "fade";
      from: Shot;
      to: Shot;
      p: number;
      color: string | null;
      veil: Veil | null;
    }
  | {
      kind: "wipe";
      from: Shot;
      to: Shot;
      p: number;
      direction: WipeDirection;
      veil: Veil | null;
    }
  | {
      kind: "carry";
      from: Shot;
      to: Shot;
      p: number;
      /** The scene whose stage is shown: `from` before the midpoint. */
      shot: Shot;
      camera: CameraPose;
      veil: Veil | null;
    };

const clamp = (x: number, lo: number, hi: number): number =>
  Math.min(hi, Math.max(lo, Number.isFinite(x) ? x : lo));

/** The latest local time of a scene that is still inside it. */
function lastMoment(seconds: number): number {
  return Math.max(0, seconds - 1e-6);
}

/** The leader's or the trailer's veil at film time `t` (§7.3: a fade from
 *  and to `color`, inside the first and the last scene). */
export function veilAt(m: Manifest, t: number, length: number): Veil | null {
  const lead = m.leader.seconds;
  if (lead > 0 && t < lead) {
    return { color: m.leader.color, alpha: 1 - clamp(t / lead, 0, 1) };
  }
  const trail = m.trailer.seconds;
  if (trail > 0 && t > length - trail) {
    return {
      color: m.trailer.color,
      alpha: clamp((t - (length - trail)) / trail, 0, 1),
    };
  }
  return null;
}

/** What is on screen at film time `t` (`focus` null), or at local time `t`
 *  of the focused scene `focus` (§7.5: one scene alone, no transition, no
 *  leader or trailer). */
export function compose(
  m: Manifest,
  segs: readonly Segment[],
  t: number,
  focus: number | null = null,
): Composition {
  if (focus !== null) {
    const seg = segs[focus];
    return {
      kind: "one",
      shot: { scene: focus, t: clamp(t, 0, lastMoment(seg.seconds)) },
      veil: null,
    };
  }
  const length = filmLength(segs);
  const ft = clamp(t, 0, Math.max(0, length - 1e-6));
  const veil = veilAt(m, ft, length);
  for (let k = 0; k < segs.length; k++) {
    const seg = segs[k];
    const next = segs[k + 1];
    if (next && ft >= next.start && ft < seg.end) {
      const transition = m.transitions[k];
      const span = seg.end - next.start;
      const p = span > 0 ? clamp((ft - next.start) / span, 0, 1) : 1;
      const from = {
        scene: k,
        t: clamp(ft - seg.start, 0, lastMoment(seg.seconds)),
      };
      const to = {
        scene: k + 1,
        t: clamp(ft - next.start, 0, lastMoment(next.seconds)),
      };
      return during(transition, from, to, p, veil);
    }
    if (ft < seg.end || k === segs.length - 1) {
      return {
        kind: "one",
        shot: {
          scene: k,
          t: clamp(ft - seg.start, 0, lastMoment(seg.seconds)),
        },
        veil,
      };
    }
  }
  return { kind: "one", shot: { scene: 0, t: 0 }, veil };
}

function during(
  transition: ManifestTransition,
  from: Shot,
  to: Shot,
  p: number,
  veil: Veil | null,
): Composition {
  if (transition.kind === "wipe") {
    return {
      kind: "wipe",
      from,
      to,
      p,
      direction: transition.direction ?? "left",
      veil,
    };
  }
  if (transition.kind === "carry" && transition.camera) {
    const local = p * transition.seconds;
    const pose = cameraAt(transition.camera.samples, local);
    // The stage is swapped at the midpoint; the camera stands where it
    // stands relative to the member the carry follows, in the scene shown.
    const anchor = transition.camera.anchors[p < 0.5 ? 0 : 1];
    const add = (v: Vec3): Vec3 => [
      v[0] + anchor[0],
      v[1] + anchor[1],
      v[2] + anchor[2],
    ];
    return {
      kind: "carry",
      from,
      to,
      p,
      shot: p < 0.5 ? from : to,
      camera: { from: add(pose.from), to: add(pose.to), fovY: pose.fovY },
      veil,
    };
  }
  // A fade, a carry without its camera, and a cut that overlaps (it never
  // does: its overlap is 0) are drawn as a fade.
  return { kind: "fade", from, to, p, color: transition.color ?? null, veil };
}

/** The scene whose captions and whose objects the moment is about: the
 *  outgoing one before a transition's midpoint, the incoming after. */
export function ownerOf(c: Composition): Shot {
  if (c.kind === "one") return c.shot;
  return c.p < 0.5 ? c.from : c.to;
}

/** Every scene the composition draws. */
export function scenesOf(c: Composition): number[] {
  if (c.kind === "one") return [c.shot.scene];
  if (c.kind === "carry") return [c.shot.scene];
  return [c.from.scene, c.to.scene];
}

/** How visible the captions are: through a fade and a wipe they leave and
 *  come back with the scenes, and the leader's or trailer's colour hides
 *  them as it hides the picture. */
export function captionOpacity(c: Composition): number {
  const veil = c.veil ? 1 - c.veil.alpha : 1;
  if (c.kind === "fade" || c.kind === "wipe") {
    return veil * Math.abs(1 - 2 * c.p);
  }
  return veil;
}

// --------------------------------------------------------------- drawing

/** A rectangle of the picture in 0..1, from the bottom-left (GL's). */
export type Box = [number, number, number, number];

/** One step of drawing a composition. `scene` draws a scene's moment into
 *  the screen or into one of two pictures (`a`, `b`), the whole of it or
 *  inside `scissor`; with `camera` set the scene is seen through that
 *  camera instead of its own (a carry). `mix` blends `a` into `b` by `k`,
 *  through a colour when `through` is set; `veil` lays a colour over the
 *  whole picture. */
export type DrawOp =
  | {
      op: "scene";
      scene: number;
      t: number;
      camera: CameraPose | null;
      target: "screen" | "a" | "b";
      scissor: Box | null;
    }
  | { op: "mix"; k: number; through: string | null }
  | { op: "veil"; color: string; alpha: number };

/** Where the incoming scene shows during a wipe going `direction` (the
 *  edge travels that way), at progress `p`. */
export function wipeBox(direction: WipeDirection, p: number): Box {
  const k = clamp(p, 0, 1);
  switch (direction) {
    case "left":
      return [1 - k, 0, k, 1];
    case "right":
      return [0, 0, k, 1];
    case "up":
      return [0, 0, 1, k];
    case "down":
      return [0, 1 - k, 1, k];
  }
}

/** The steps that draw `c`, in order. */
export function drawOps(c: Composition): DrawOp[] {
  const ops: DrawOp[] = [];
  const scene = (
    shot: Shot,
    target: "screen" | "a" | "b",
    camera: CameraPose | null = null,
    scissor: Box | null = null,
  ): DrawOp => ({
    op: "scene",
    scene: shot.scene,
    t: shot.t,
    camera,
    target,
    scissor,
  });
  switch (c.kind) {
    case "one":
      ops.push(scene(c.shot, "screen"));
      break;
    case "fade":
      ops.push(scene(c.from, "a"), scene(c.to, "b"));
      ops.push({ op: "mix", k: c.p, through: c.color });
      break;
    case "wipe":
      ops.push(scene(c.from, "screen"));
      ops.push(scene(c.to, "screen", null, wipeBox(c.direction, c.p)));
      break;
    case "carry":
      ops.push(scene(c.shot, "screen", c.camera));
      break;
  }
  if (c.veil && c.veil.alpha > 0) {
    ops.push({ op: "veil", color: c.veil.color, alpha: c.veil.alpha });
  }
  return ops;
}

/** A fade's blend at `k` as weights `[a, colour, b]`: straight across
 *  without a colour; through the colour (all of it at the midpoint)
 *  with one. Both the engine's shader and the tests read this. */
export function mixWeights(
  k: number,
  through: string | null,
): [number, number, number] {
  const p = clamp(k, 0, 1);
  if (through === null) return [1 - p, 0, p];
  if (p < 0.5) return [1 - 2 * p, 2 * p, 0];
  return [0, 2 - 2 * p, 2 * p - 1];
}

// --------------------------------------------------------------- samples

/** The two rows around `t` and how far between them; held past the ends. */
export function bracket(
  times: ArrayLike<number>,
  t: number,
): { i0: number; i1: number; k: number } {
  const n = times.length;
  if (n === 0) return { i0: 0, i1: 0, k: 0 };
  if (!(t > times[0])) return { i0: 0, i1: 0, k: 0 };
  if (t >= times[n - 1]) return { i0: n - 1, i1: n - 1, k: 0 };
  let lo = 0;
  let hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (times[mid] <= t) lo = mid;
    else hi = mid;
  }
  const span = times[hi] - times[lo];
  return { i0: lo, i1: hi, k: span > 0 ? (t - times[lo]) / span : 0 };
}

const lerp = (a: number, b: number, k: number): number => a + (b - a) * k;
const lerp3 = (a: Vec3, b: Vec3, k: number): Vec3 => [
  lerp(a[0], b[0], k),
  lerp(a[1], b[1], k),
  lerp(a[2], b[2], k),
];

/** The camera of `[t, from, to, fov_y]` rows at local time `t`. */
export function cameraAt(rows: readonly CameraRow[], t: number): CameraPose {
  if (rows.length === 0) return { from: [0, -2, 1], to: [0, 0, 0], fovY: 35 };
  const { i0, i1, k } = bracket(
    rows.map((row) => row[0]),
    t,
  );
  const a = rows[i0];
  const b = rows[i1];
  return {
    from: lerp3(a[1], b[1], k),
    to: lerp3(a[2], b[2], k),
    fovY: lerp(a[3], b[3], k),
  };
}

/** An object's transform at local time `t` (a person's root: `amount` 0). */
export interface ObjectPose {
  position: Vec3;
  yaw: number;
  scale: number;
  amount: number;
}

/** The same lerp over an object's five-column rows and a person's
 *  four-column ones (P29: a person has no pose amount). */
export function objectAt(
  rows: readonly (ObjectRow | PersonRow)[],
  t: number,
): ObjectPose {
  if (rows.length === 0)
    return { position: [0, 0, 0], yaw: 0, scale: 1, amount: 0 };
  const { i0, i1, k } = bracket(
    rows.map((row) => row[0]),
    t,
  );
  const a = rows[i0];
  const b = rows[i1];
  return {
    position: lerp3(a[1], b[1], k),
    yaw: lerp(a[2], b[2], k),
    scale: lerp(a[3], b[3], k),
    amount: lerp(a[4] ?? 0, b[4] ?? 0, k),
  };
}

// --------------------------------------------------------------- the transport

/** The player's state. `t` is film time, or the focused scene's local
 *  time when `focus` is set. */
export interface Clock {
  t: number;
  playing: boolean;
  /** The film starts again at its end (a focused scene always loops). */
  loop: boolean;
  focus: number | null;
}

export type ClockAction =
  | { type: "play" }
  | { type: "pause" }
  | { type: "toggle" }
  /** Film time; while focused, the focused scene's moment at that film
   *  time (clamped to the scene). */
  | { type: "seek"; t: number }
  | { type: "focus"; scene: number | null }
  /** Real seconds since the last frame drawn. */
  | { type: "tick"; dt: number };

/** The longest step one animation frame may take (the live player's rule). */
export const MAX_STEP_S = 1;

export const CLOCK_START: Clock = {
  t: 0,
  playing: false,
  loop: true,
  focus: null,
};

/** Every rule of the transport. Focus puts the clock at the start of the
 *  scene and loops it; leaving focus puts the film where that scene's
 *  moment is on the film's clock. */
export function clock(
  s: Clock,
  a: ClockAction,
  segs: readonly Segment[],
): Clock {
  const length = filmLength(segs);
  switch (a.type) {
    case "play":
      return s.playing ? s : { ...s, playing: true };
    case "pause":
      return s.playing ? { ...s, playing: false } : s;
    case "toggle":
      return { ...s, playing: !s.playing };
    case "seek": {
      if (s.focus !== null) {
        const seg = segs[s.focus];
        return { ...s, t: clamp(a.t - seg.start, 0, lastMoment(seg.seconds)) };
      }
      return { ...s, t: clamp(a.t, 0, Math.max(0, length - 1e-6)) };
    }
    case "focus": {
      if (a.scene === s.focus) return s;
      if (a.scene === null) {
        const seg = s.focus === null ? null : segs[s.focus];
        const t = seg ? seg.start + s.t : s.t;
        return {
          ...s,
          focus: null,
          t: clamp(t, 0, Math.max(0, length - 1e-6)),
        };
      }
      if (a.scene < 0 || a.scene >= segs.length) return s;
      return { ...s, focus: a.scene, t: 0 };
    }
    case "tick": {
      if (!s.playing) return s;
      const dt = clamp(a.dt, 0, MAX_STEP_S);
      const span = s.focus === null ? length : segs[s.focus].seconds;
      const t = s.t + dt;
      if (t < span) return { ...s, t };
      if (s.focus !== null || s.loop)
        return { ...s, t: span > 0 ? t % span : 0 };
      return { ...s, playing: false, t: Math.max(0, span - 1e-6) };
    }
  }
}

/** The film time of the clock's moment. */
export function filmTimeOf(s: Clock, segs: readonly Segment[]): number {
  return s.focus === null ? s.t : segs[s.focus].start + s.t;
}

/** The scenes to have ready for a composition: what it draws, and the scene
 *  after the last of them (the next one is preloaded). */
export function wanted(
  c: Composition,
  count: number,
  focus: number | null,
): number[] {
  const drawn = scenesOf(c);
  if (focus !== null) return drawn;
  const next = Math.max(...drawn) + 1;
  return next < count ? [...drawn, next] : drawn;
}

// --------------------------------------------------------------- for people

/** The picture's size in pixels at an aspect: 1080 on the shorter side. */
export function resolutionOf(aspect: string): [number, number] {
  if (aspect === "9:16") return [1080, 1920];
  if (aspect === "16:9") return [1920, 1080];
  return [1080, 1080];
}

/** `m:ss.d` (`0:07.5`), as the live player writes time. */
export function formatTime(t: number): string {
  const tenths = Math.max(0, Math.round((Number.isFinite(t) ? t : 0) * 10));
  const minutes = Math.floor(tenths / 600);
  const rest = tenths - minutes * 600;
  const seconds = Math.floor(rest / 10);
  return `${minutes}:${String(seconds).padStart(2, "0")}.${rest % 10}`;
}
